import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { StatusModel } from '@core/models/status.model';
import * as fromRoot from '@core/redux';
import { LoadProjectStatusesAction } from '@core/redux/status/status.actions';
import { Store } from '@ngrx/store';

@Component({
  selector: 'ux-popup-status',
  templateUrl: './popup-status.component.html',
  styleUrls: ['./popup-status.component.less'],
  imports: [ReactiveFormsModule, CommonModule],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopupStatusComponent implements AfterViewChecked, OnInit {
  @ViewChild('filterInput') filterInput: ElementRef;

  // Input signals
  header = input<string>('Статус задачи');
  multiple = input<boolean>(false);
  projectId = input<string>('');
  value = input<string | string[]>('');

  private readonly store = inject<Store<fromRoot.State>>(Store);
  private lastLoadedProjectId: string | null = null;

  // Output signals
  selectItem = output<StatusModel>();
  changeItems = output<string[]>();
  onClose = output<void>();

  protected readonly searchControl = new FormControl('');
  protected readonly selectIndex = signal<number>(-1);
  protected readonly selectedValues = signal<string[]>([]);
  protected readonly searchValue = signal('');

  protected readonly availableStatuses = signal<StatusModel[]>([]);

  // Преобразуем valueChanges в сигнал
  private readonly searchControlValue = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  constructor() {
    // Следим за изменениями value
    effect(() => {
      const currentValue = this.value();
      if (currentValue) {
        this.selectedValues.set(Array.isArray(currentValue) ? [...currentValue] : [currentValue]);
      } else {
        this.selectedValues.set([]);
      }
    });

    // Следим за изменениями в поиске
    effect(() => {
      this.searchValue.set(this.searchControlValue() || '');
    });

    // Следим за статусами из store
    effect(() => {
      const state = this.store.selectSignal(fromRoot.getStatusEntities);
      this.availableStatuses.set(state());
    });
  }

  ngOnInit() {
    if (this.projectId()) {
      this.loadStatuses(this.projectId());
    }
  }

  private loadStatuses(projectId: string) {
    this.lastLoadedProjectId = projectId;
    this.store.dispatch(new LoadProjectStatusesAction(projectId));
  }

  ngAfterViewChecked() {
    if (this.filterInput) {
      this.filterInput.nativeElement.focus();
    }
  }

  protected readonly filteredItems = computed(() => {
    const searchTerm = this.searchValue()?.toLowerCase() || '';
    const statuses = this.availableStatuses();
    const currentProjectId = this.projectId();

    return statuses.filter(status => {
      // Filter by search term
      const matchesSearch = !searchTerm || status.name.toLowerCase().includes(searchTerm);

      // Filter by project ID
      const matchesProject = status.projectId === currentProjectId;

      return matchesSearch && matchesProject;
    });
  });

  protected hasSelected = (id: string): boolean => this.selectedValues().includes(id);

  protected onSelect(item: StatusModel): void {
    if (!item?.id) {
      return;
    }

    this.selectedValues.update(currentValues => {
      const newValues = this.multiple() ? this.toggleSelection(currentValues, item.id) : [item.id];

      this.changeItems.emit(newValues);
      this.selectItem.emit(item);
      return newValues;
    });
  }

  private toggleSelection(values: string[], id: string): string[] {
    const index = values.indexOf(id);
    return index > -1 ? values.filter((_, i) => i !== index) : [...values, id];
  }

  protected onArrowUp(): void {
    this.moveSelection(-1);
  }

  protected onArrowDown(): void {
    this.moveSelection(1);
  }

  private moveSelection(direction: number): void {
    this.selectIndex.update(current => {
      const items = this.filteredItems();
      const newIndex = current + direction;

      if (newIndex < 0) {
        return items.length - 1;
      }
      if (newIndex >= items.length) {
        return 0;
      }
      return newIndex;
    });
  }

  protected onEnterPress(): void {
    const index = this.selectIndex();
    const items = this.filteredItems();

    if (index >= 0 && index < items.length) {
      this.onSelect(items[index]);
    }
  }
}
